import React from "react";
import { Box, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import BlockIcon from "@mui/icons-material/Block";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";

/**
 * Canonical target-status colour + icon system for the ABA workflow
 * editor. `masteredTargetStatus` is a **target status** (what happens
 * to the target after mastery), NOT a workflow phase — so it must
 * render with its own semantic tone, not the mint "mastered event"
 * colour.
 *
 * Consumed by:
 *   - PhaseCard collapsed header (`Mastered → <status>` chip)
 *   - TransitionsSummary bottom section
 *   - any future workflow map that surfaces mastered outcomes
 *
 * Keep this the single source of truth; do NOT hardcode a status
 * colour in a component directly.
 */

type Tone = {
  bg: string;
  border: string;
  text: string;
  icon: string;
  Icon: React.ComponentType<{ sx?: any }>;
  label: string;
};

const TARGET_STATUS_TONES: Record<string, Tone> = {
  active: {
    bg: "#EAF2FF",
    border: "#BFDBFE",
    text: "#1D4ED8",
    icon: "#2563EB",
    Icon: CheckIcon,
    label: "Active",
  },
  maintenance: {
    bg: "#F3E8FF",
    border: "#D8B4FE",
    text: "#7E22CE",
    icon: "#8B5CF6",
    Icon: PauseCircleOutlineIcon,
    label: "Maintenance",
  },
  closed: {
    bg: "#FEF2F2",
    border: "#FECACA",
    text: "#DC2626",
    icon: "#EF4444",
    Icon: BlockIcon,
    label: "Closed",
  },
  discontinued: {
    bg: "#F3F4F6",
    border: "#D1D5DB",
    text: "#4B5563",
    icon: "#6B7280",
    Icon: RemoveCircleOutlineIcon,
    label: "Discontinued",
  },
};

/** Neutral fallback for any unknown/legacy value — never green. */
const NEUTRAL_TONE: Tone = {
  bg: "#F1F5F9",
  border: "#CBD5E1",
  text: "#334155",
  icon: "#475569",
  Icon: CheckIcon,
  label: "Status",
};

export const targetStatusTone = (status: string | null | undefined): Tone => {
  const key = String(status ?? "").trim().toLowerCase();
  return TARGET_STATUS_TONES[key] ?? { ...NEUTRAL_TONE, label: key ? key.charAt(0).toUpperCase() + key.slice(1) : "Status" };
};

/**
 * The mastered-condition tag ("Mastered →") uses a small mint pill.
 * Kept separate from the target-status colour so it doesn't compete
 * with the destination status chip visually.
 */
export const MASTERED_CONDITION_TONE = {
  bg: "#ECFDF5",
  border: "#A7F3D0",
  text: "#047857",
  icon: "#059669",
};

/**
 * Rendered pair used by both PhaseCard header + TransitionsSummary.
 * `Mastered →` mint condition tag, followed by the target-status
 * chip in its semantic colour.
 */
export const MasteredOutcomeChips: React.FC<{
  status: string | null | undefined;
  size?: "sm" | "md";
}> = ({ status, size = "sm" }) => {
  const tone = targetStatusTone(status);
  const StatusIcon = tone.Icon;
  const py = size === "sm" ? 0.25 : 0.4;
  const px = size === "sm" ? 0.85 : 1;
  const fs = size === "sm" ? 12 : 12.5;
  return (
    <>
      <Box
        sx={{
          display: "inline-flex", alignItems: "center", gap: 0.4,
          px, py,
          borderRadius: 1,
          backgroundColor: MASTERED_CONDITION_TONE.bg,
          color: MASTERED_CONDITION_TONE.text,
          border: `1px solid ${MASTERED_CONDITION_TONE.border}`,
        }}
      >
        <CheckIcon sx={{ fontSize: 13, color: MASTERED_CONDITION_TONE.icon }} />
        <Typography sx={{ fontSize: fs, fontWeight: 700 }}>Mastered</Typography>
      </Box>
      <Box
        sx={{
          display: "inline-flex", alignItems: "center", gap: 0.4,
          px, py,
          borderRadius: 1,
          backgroundColor: tone.bg,
          color: tone.text,
          border: `1px solid ${tone.border}`,
        }}
      >
        <StatusIcon sx={{ fontSize: 13, color: tone.icon }} />
        <Typography sx={{ fontSize: fs, fontWeight: 700 }}>{tone.label}</Typography>
      </Box>
    </>
  );
};

/** Small stand-alone status chip — used inside dropdowns / summary. */
export const TargetStatusChip: React.FC<{ status: string | null | undefined }> = ({ status }) => {
  const tone = targetStatusTone(status);
  const StatusIcon = tone.Icon;
  return (
    <Box
      sx={{
        display: "inline-flex", alignItems: "center", gap: 0.4,
        px: 0.85, py: 0.25,
        borderRadius: 1,
        backgroundColor: tone.bg,
        color: tone.text,
        border: `1px solid ${tone.border}`,
      }}
    >
      <StatusIcon sx={{ fontSize: 13, color: tone.icon }} />
      <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{tone.label}</Typography>
    </Box>
  );
};
